/**
 * @file AgentPanel.jsx
 * @description AgentPanel component for displaying and managing agent panel.
 * @component AgentPanel
 * @exports AgentPanel - Main component for displaying and managing agent panel.
 */

import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid2";
import { Box, Typography, Container, Button, Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, RadioGroup, Radio, Divider } from "@mui/material";
import { SKILL_TYPES } from "../../../common/constants/adminModule/skillConstants.js";
import { STATUS_STRINGS } from "../../../common/constants/agentModule/statusConstants.js";
import { BUTTON_COLORS, VARIANTS, SIZES } from "../../../common/constants/commonConstants.js";

const AgentPanel = ({
    userSkills,
    agentStatus,
    offlineReason,
    handleOffline,
    setAgentStatus,
    selectedSkills,
    offlineReasons,
    handleAvailable,
    setOfflineReason,
    setSelectedSkills,
    handleSkillChange,
}) => {

    const [skillType, setSkillType] = useState('');
    useEffect(() => {
        if (userSkills.length === 0) return;
        const hasChat = userSkills.some(
            (userSkill) => !userSkill.isVoice && userSkill.queueIdreq === 1 && selectedSkills.includes(userSkill.skillname)
        );
        const hasCalls = userSkills.some(
            (userSkill) => userSkill.isVoice && userSkill.queueIdreq === 1 && selectedSkills.includes(userSkill.skillname)
        );
        if (hasChat) setSkillType(SKILL_TYPES.CHAT);
        else if (hasCalls) setSkillType(SKILL_TYPES.CALLS);
        else setSkillType(SKILL_TYPES.CALLS);
    }, [userSkills, selectedSkills]);

    const handleSkillChangeWithReset = (skillName) => {
        const skillObj = userSkills.find((userSkill) => userSkill.skillname === skillName);
        if (skillObj?.queueIdreq === 1) {
            if (skillType === SKILL_TYPES.CALLS && skillObj.isVoice) {
                const skillsToKeep = selectedSkills.filter((name) => {
                    const skill = userSkills.find((userSkill) => userSkill.skillname === name);
                    return skill?.queueIdreq === 0 || skill?.isVoice;
                });
                setSelectedSkills(skillsToKeep);
            }
            if (skillType === SKILL_TYPES.CHAT && !skillObj.isVoice) {
                const skillsToKeep = selectedSkills.filter((name) => {
                    const userSkill = userSkills.find((userSkill) => userSkill.skillname === name);
                    return userSkill?.queueIdreq === 0 || !userSkill?.isVoice;
                });
                setSelectedSkills(skillsToKeep);
            }
        }
        handleSkillChange(skillName);
    };

    return (
        <>
            <Container maxWidth={false} disableGutters>
                <Grid container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
                    <Grid item>
                        <Typography variant="h6">{STATUS_STRINGS.AGENT_PANEL}</Typography>
                    </Grid>
                </Grid>
                {agentStatus && (
                    <>
                        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                            <Button
                                variant={agentStatus === STATUS_STRINGS.AVAILABLE ? VARIANTS.CONTAINED : VARIANTS.OUTLINED}
                                color={BUTTON_COLORS.SUCCESS}
                                onClick={() => setAgentStatus(STATUS_STRINGS.AVAILABLE)}
                                size={SIZES.SMALL}
                            >
                                {STATUS_STRINGS.AVAILABLE}
                            </Button>
                            <Button
                                variant={agentStatus === STATUS_STRINGS.OFFLINE ? VARIANTS.CONTAINED : VARIANTS.OUTLINED}
                                color={BUTTON_COLORS.ERROR}
                                onClick={() => setAgentStatus(STATUS_STRINGS.OFFLINE)}
                                size={SIZES.SMALL}
                            >
                                {STATUS_STRINGS.OFFLINE}
                            </Button>
                        </Box>
                        {agentStatus === STATUS_STRINGS.AVAILABLE && (
                            <>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                    {STATUS_STRINGS.SKILLS}
                                </Typography>
                                <FormGroup>
                                    {userSkills
                                        .filter(skill => skill.queueIdreq === 0)
                                        .map(skill => (
                                            <FormControlLabel
                                                slotProps={{ typography: { variant: "body2" } }}
                                                key={skill.id}
                                                control={
                                                    <Checkbox
                                                        checked={selectedSkills.includes(skill.skillname)}
                                                        onChange={() => handleSkillChange(skill.skillname)}
                                                    />
                                                }
                                                label={skill.skillname}
                                            />
                                        ))}
                                </FormGroup>
                                <Divider sx={{ my: 1 }} />
                                {skillType && (
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                        <RadioGroup
                                            row
                                            value={skillType}
                                            onChange={(e) => setSkillType(e.target.value)}
                                        >
                                            <FormControlLabel slotProps={{ typography: { variant: "body2" } }} value={SKILL_TYPES.CALLS} control={<Radio />} label={SKILL_TYPES.CALLS} />
                                            <FormControlLabel slotProps={{ typography: { variant: "body2" } }} value={SKILL_TYPES.CHAT} control={<Radio />} label={SKILL_TYPES.CHAT} />
                                        </RadioGroup>
                                    </Box>
                                )}
                                <FormGroup>
                                    {userSkills
                                        .filter(skill => skill.queueIdreq === 1)
                                        .filter(skill =>
                                            skillType === SKILL_TYPES.CALLS ? skill.isVoice : !skill.isVoice
                                        )
                                        .map(skill => (
                                            <FormControlLabel
                                                key={skill.id}
                                                slotProps={{ typography: { variant: "body2" } }}
                                                control={
                                                    <Checkbox
                                                        checked={selectedSkills.includes(skill.skillname)}
                                                        onChange={() => handleSkillChangeWithReset(skill.skillname)}
                                                    />
                                                }
                                                label={skill.skillname}
                                            />
                                        ))}
                                </FormGroup>
                            </>
                        )}
                        {agentStatus === STATUS_STRINGS.OFFLINE && (
                            <FormControl fullWidth size={SIZES.SMALL}>
                                <InputLabel id={STATUS_STRINGS.REASON} sx={{ fontSize: "0.875rem" }}>{STATUS_STRINGS.REASON}</InputLabel>
                                <Select
                                    labelId={STATUS_STRINGS.REASON}
                                    value={offlineReason}
                                    label={STATUS_STRINGS.REASON}
                                    onChange={(e) => setOfflineReason(e.target.value)}
                                    sx={{ "& .MuiSelect-select": { fontSize: "0.875rem" } }}
                                >
                                    {offlineReasons.map((reason, index) => (
                                        <MenuItem key={index} sx={{ fontSize: "0.875rem" }} value={reason}>{reason}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Button
                            variant={VARIANTS.CONTAINED}
                            color={BUTTON_COLORS.PRIMARY}
                            size={SIZES.SMALL}
                            onClick={
                                agentStatus === STATUS_STRINGS.AVAILABLE
                                    ? handleAvailable
                                    : handleOffline
                            }
                            sx={{ mt: 2 }}
                        >
                            {STATUS_STRINGS.SAVE}
                        </Button>
                    </>
                )}
            </Container>
        </>
    );
};

export default AgentPanel;